<template>
  <div class="w-full">
    <main class="mx-auto max-w-7xl px-5 sm:px-8">
      <!-- ───────────────────────── Hero ───────────────────────── -->
      <section
        class="relative grid grid-cols-1 items-center gap-8 pt-8 pb-12 sm:gap-12 sm:pt-10 sm:pb-24 lg:grid-cols-12 lg:gap-8 lg:pt-20"
      >
        <HeroFish
          v-if="!reduceMotion"
          class="pointer-events-none absolute inset-0 hidden lg:block"
        />
        <div class="min-w-0 lg:col-span-6">
          <p
            ref="eyebrow"
            class="mb-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-rose-400/90 opacity-0"
          >
            <span class="h-1.5 w-1.5 rounded-full bg-rose-500"></span>
            Peer-to-peer mesh VPN
          </p>

          <h1 class="font-extrabold tracking-tight">
            <span
              ref="line1"
              class="block text-[2rem] leading-tight text-zinc-50 opacity-0 sm:text-5xl xl:text-6xl"
            >
              Your devices, on one
            </span>
            <span
              ref="line2"
              class="mt-1 block text-[2rem] leading-tight text-rose-600 opacity-0 sm:text-5xl xl:text-6xl"
            >
              private network.
            </span>
          </h1>

          <p
            ref="subtext"
            class="mt-7 max-w-xl text-lg font-light leading-relaxed text-zinc-100/75 opacity-0"
          >
            Connect your computers, servers and phones as if they were on the
            same LAN, wherever they are. Rayfish is a peer-to-peer VPN that
            links them directly, with no port forwarding or relay to trust.
          </p>

          <div ref="cta" class="mt-10 opacity-0">
            <div class="flex flex-wrap items-center gap-4">
              <a
                v-if="installer.kind === 'download'"
                :href="installer.href"
                class="group inline-flex items-center gap-2 rounded-full bg-rose-600 px-6 py-3 text-sm font-semibold text-white shadow-sm transition duration-300 hover:bg-rose-500 hover:shadow-rose-500/20 hover:shadow-lg"
              >
                <svg
                  class="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="2"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M12 3v12m0 0 4-4m-4 4-4-4M5 19h14"
                  />
                </svg>
                {{ installer.label }}
              </a>
              <div
                v-else
                class="flex min-w-0 max-w-full items-center gap-3 rounded-2xl border border-zinc-700/60 bg-zinc-800/40 py-1.5 pl-3 pr-1.5 sm:pl-5"
              >
                <code
                  class="min-w-0 flex-1 overflow-x-auto whitespace-nowrap font-mono text-xs sm:text-sm text-zinc-300"
                >
                  <span class="select-none text-rose-400/80">{{
                    installer.prompt
                  }}</span
                  >{{ installer.cmd }}
                </code>
                <button
                  type="button"
                  @click="copyInstall"
                  :aria-label="copied ? 'Copied' : 'Copy install command'"
                  class="inline-flex h-11 w-11 shrink-0 sm:h-8 sm:w-8 items-center justify-center rounded-full text-zinc-400 transition-colors hover:bg-zinc-700/60 hover:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-rose-500/60"
                >
                  <svg
                    v-if="!copied"
                    class="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="2"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M8 7V5a2 2 0 012-2h8a2 2 0 012 2v8a2 2 0 01-2 2h-2M5 9h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2v-8a2 2 0 012-2z"
                    />
                  </svg>
                  <svg
                    v-else
                    class="h-4 w-4 text-rose-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="2.5"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M4.5 12.75l6 6 9-13.5"
                    />
                  </svg>
                </button>
              </div>
              <a
                href="https://github.com/rayfish/rayfish"
                target="_blank"
                rel="noopener"
                class="group inline-flex items-center gap-2 rounded-full bg-zinc-100 px-6 py-3 text-sm font-semibold text-zinc-900 shadow-sm transition duration-300 hover:bg-white hover:shadow-rose-500/20 hover:shadow-lg"
              >
                <svg
                  class="h-5 w-5"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"
                  />
                </svg>
                View on GitHub
              </a>
            </div>
            <p class="mt-3 max-w-xl text-sm text-zinc-400">
              {{ installer.detail }}
              <router-link
                to="/docs/02-getting-started#installing"
                class="text-rose-400 hover:underline"
                >Installation guide</router-link
              >.
            </p>
            <div
              class="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-zinc-500"
            >
              <span>Other installs:</span>
              <button
                v-for="option in otherInstallers"
                :key="option.os"
                type="button"
                @click="selectInstaller(option.os)"
                class="inline-flex min-h-[44px] items-center text-zinc-400 underline decoration-zinc-600 underline-offset-4 transition-colors hover:text-zinc-200 hover:decoration-zinc-400"
              >
                {{ option.option }}
              </button>
            </div>
          </div>
        </div>

        <!-- Signature: a live terminal of the real `ray` workflow -->
        <div ref="cloud" class="relative min-w-0 opacity-0 lg:col-span-6">
          <div
            class="relative overflow-hidden rounded-2xl border border-zinc-700/50 bg-zinc-900/70 shadow-2xl shadow-black/40 backdrop-blur"
          >
            <div
              class="flex items-center gap-2 border-b border-zinc-700/40 bg-zinc-800/50 px-4 py-3"
            >
              <span class="h-3 w-3 rounded-full bg-zinc-600"></span>
              <span class="h-3 w-3 rounded-full bg-zinc-600"></span>
              <span class="h-3 w-3 rounded-full bg-zinc-600"></span>
              <span class="ml-3 font-mono text-xs text-zinc-500"
                >alice@laptop, ray</span
              >
            </div>
            <div
              class="overflow-x-auto px-4 py-5 font-mono sm:px-5 text-[13px] leading-relaxed sm:text-sm"
            >
              <p class="text-zinc-100">
                <span class="text-rose-400">$</span> ray create --hostname
                alice<span
                  v-if="!output"
                  class="ml-0.5 inline-block h-4 w-[7px] translate-y-0.5 animate-blink bg-rose-400"
                ></span>
              </p>

              <div v-if="output" class="mt-2 space-y-0.5 text-zinc-400">
                <p>
                  <span class="text-emerald-400">Network created:</span>
                  <span class="text-zinc-200"> gentle-amber-fox</span>
                </p>
                <p>
                  <span class="text-zinc-500">IPv6</span
                  >&nbsp;&nbsp;200:ab3f:d92c:1e4a::1
                </p>
                <p>
                  <span class="text-zinc-500">room id</span
                  >&nbsp;&nbsp;3f8a…c7d2
                </p>
                <p class="pt-2 text-zinc-100">
                  <span class="text-rose-400">$</span> ray invite
                  gentle-amber-fox
                  <span class="text-zinc-500"># share the invite code</span>
                </p>
              </div>

              <p v-if="output && peers.length" class="mt-3 text-zinc-600">
                # peers joining the mesh
              </p>
              <transition-group
                tag="div"
                class="mt-1 space-y-1"
                enter-active-class="transition duration-500 ease-out"
                enter-from-class="opacity-0 -translate-x-2"
                enter-to-class="opacity-100 translate-x-0"
              >
                <p
                  v-for="p in peers"
                  :key="p.name"
                  class="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-zinc-300"
                >
                  <span class="relative flex h-2 w-2 shrink-0">
                    <span
                      class="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60"
                    ></span>
                    <span
                      class="relative inline-flex h-2 w-2 rounded-full bg-emerald-400"
                    ></span>
                  </span>
                  <span class="text-zinc-100">{{ p.name }}</span>
                  <span class="text-zinc-500">{{ p.note }}</span>
                  <span v-if="p.rtt" class="ml-auto text-emerald-400/80">{{
                    p.rtt
                  }}</span>
                </p>
              </transition-group>

              <p v-if="done" class="mt-3 text-zinc-100">
                <span class="text-rose-400">$</span>
                <span
                  class="ml-1 inline-block h-4 w-[7px] translate-y-0.5 animate-blink bg-rose-400"
                ></span>
              </p>
            </div>
          </div>
        </div>
      </section>

      <!-- ──────────────────── How it works (a real sequence) ──────────────────── -->
      <section class="border-t border-zinc-800/80 py-12 sm:py-20">
        <h2
          class="reveal mb-8 sm:mb-14 text-center font-mono text-xs uppercase tracking-[0.25em] text-zinc-500"
        >
          From zero to connected in three steps
        </h2>
        <ol class="grid grid-cols-1 gap-10 md:grid-cols-3">
          <li v-for="(step, i) in steps" :key="step.title" class="reveal">
            <div class="mb-4 flex items-baseline gap-3">
              <span class="font-logo text-sm text-rose-600">0{{ i + 1 }}</span>
              <span class="h-px flex-1 bg-zinc-800"></span>
            </div>
            <h3 class="mb-2 text-xl font-semibold text-zinc-100">
              {{ step.title }}
            </h3>
            <p class="text-zinc-400">{{ step.body }}</p>
          </li>
        </ol>
      </section>

      <!-- ──────────────────────── Features ──────────────────────── -->
      <section class="border-t border-zinc-800/80 py-12 sm:py-20">
        <div class="reveal mb-8 sm:mb-14 max-w-2xl">
          <h2
            class="text-3xl font-bold tracking-tight text-zinc-50 sm:text-4xl"
          >
            Everything runs peer-to-peer.
          </h2>
          <p class="mt-4 text-lg text-zinc-400">
            Built on
            <a
              href="https://iroh.computer"
              target="_blank"
              rel="noopener"
              class="text-rose-400 hover:underline"
              >iroh</a
            >. Every peer connects directly. The coordinator only hands out
            addresses and never sees your traffic.
          </p>
        </div>
        <div
          class="grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-zinc-800/80 bg-zinc-800/40 sm:grid-cols-2 lg:grid-cols-3"
        >
          <div
            v-for="f in features"
            :key="f.title"
            class="reveal group bg-zinc-900/80 p-5 sm:p-7 transition-colors duration-300 hover:bg-zinc-800/60"
          >
            <div
              class="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-rose-600/10 text-rose-400 transition-transform duration-300 group-hover:scale-110"
              v-html="f.icon"
            ></div>
            <h3 class="mb-1.5 font-semibold text-zinc-100">{{ f.title }}</h3>
            <p class="text-sm leading-relaxed text-zinc-400">{{ f.body }}</p>
          </div>
        </div>
      </section>

      <!-- ──────────────────────── Closing CTA ──────────────────────── -->
      <section class="reveal py-12 text-center sm:py-20">
        <h2 class="text-3xl font-bold tracking-tight text-zinc-50 sm:text-4xl">
          Spin up your mesh tonight.
        </h2>
        <p class="mx-auto mt-4 max-w-md text-zinc-400">
          Free and open source. Available on Linux, macOS, Windows, Android, and
          FreeBSD. Desktop apps for macOS and Windows.
        </p>
        <a
          href="https://github.com/rayfish/rayfish"
          target="_blank"
          rel="noopener"
          class="mt-8 inline-flex items-center gap-2 rounded-full bg-rose-600 px-7 py-3.5 text-sm font-semibold text-white shadow-lg shadow-rose-600/20 transition duration-300 hover:bg-rose-500"
        >
          Get Rayfish
        </a>
      </section>
    </main>
  </div>
</template>

<script>
import { gsap } from "gsap";
import HeroFish from "@/components/HeroFish";
import { latestMacosDownload } from "@/releases";

// The available installers, keyed by the platform they belong to. `prompt` is
// the shell's, not part of the command, and the copy button leaves it out.
const INSTALLERS = {
  macos: {
    os: "macos",
    kind: "download",
    href: "https://github.com/rayfish/rayfish/releases/latest",
    label: "Download for macOS",
    option: "macOS app (DMG)",
    detail:
      "Apple Silicon. Drag into Applications, open Rayfish, and approve the network extension.",
  },
  unix: {
    os: "unix",
    kind: "command",
    prompt: "$ ",
    cmd: "curl -fsSL https://rayfish.xyz/install.sh | sh",
    option: "Linux / FreeBSD / macOS CLI",
    detail:
      "Standalone CLI and daemon. Run sudo ray up after installation. macOS requires Apple Silicon.",
  },
  windows: {
    os: "windows",
    kind: "download",
    href: "https://github.com/rayfish/rayfish/releases/latest/download/ray-windows-x86_64.msi",
    label: "Download for Windows",
    option: "Windows app (x64 MSI)",
    detail:
      "x64 desktop app, tray controls, CLI, and background service. Run the MSI, then open Rayfish from Start. ARM64 uses the PowerShell installer.",
  },
  powershell: {
    os: "powershell",
    kind: "command",
    prompt: "PS> ",
    cmd: "irm https://rayfish.xyz/install.ps1 | iex",
    option: "Windows PowerShell (x64 / ARM64)",
    detail:
      "Run in PowerShell as Administrator. x64 installs the desktop MSI; ARM64 installs the CLI and needs ray install afterwards.",
  },
};

// User-Agent Client Hints where the browser has them, the UA string otherwise.
// Neither is trustworthy, and the cost of guessing wrong is a command that
// cannot run on the machine reading it, so the other one is on the page either
// way: a wrong guess costs a click rather than a trip to the docs.
function detectOs() {
  const hinted =
    (navigator.userAgentData && navigator.userAgentData.platform) ||
    navigator.platform ||
    "";
  const userAgent = navigator.userAgent || "";
  if (/win/i.test(hinted) || /win(dows|16|32|64|9x|nt|ce)/i.test(userAgent)) {
    return "windows";
  }
  const touchMac = /mac/i.test(hinted) && navigator.maxTouchPoints > 1;
  if (
    !touchMac &&
    (/mac/i.test(hinted) || /Macintosh|Mac OS X/i.test(userAgent))
  ) {
    return "macos";
  }
  return "unix";
}

export default {
  name: "HomeView",

  components: { HeroFish },

  data() {
    return {
      reduceMotion:
        window.matchMedia &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches,
      output: false,
      done: false,
      copied: false,
      macosDownloadUrl: INSTALLERS.macos.href,
      // Which installer the pill offers: guessed from the browser, and the
      // reader's to change with the line under it.
      os: detectOs(),
      peers: [],
      steps: [
        {
          title: "Create a network",
          body: "Run ray create to start a private network. You become its coordinator and get a memorable three-word name.",
        },
        {
          title: "Share an invite code",
          body: "Run ray invite with the network name, then share its one-time code. The other device joins with ray join and that code.",
        },
        {
          title: "You're connected",
          body: "Every peer gets a stable virtual IP and a .ray hostname. Any app that speaks TCP or UDP just works, like a local LAN.",
        },
      ],
      features: [
        {
          title: "Full mesh",
          body: "Every peer connects directly to every other peer. No central hub to bottleneck or snoop on your traffic.",
          icon: `<svg class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="1.7" viewBox="0 0 24 24"><circle cx="12" cy="5" r="2"/><circle cx="5" cy="18" r="2"/><circle cx="19" cy="18" r="2"/><path stroke-linecap="round" d="M12 7v3m0 0l-5.5 6.5M12 10l5.5 6.5M7 18h10"/></svg>`,
        },
        {
          title: "Encrypted by identity",
          body: "Peers are addressed by their Ed25519 public key, not passwords or shared secrets. Addresses are stable forever.",
          icon: `<svg class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="1.7" viewBox="0 0 24 24"><rect x="4" y="10" width="16" height="10" rx="2"/><path stroke-linecap="round" d="M8 10V7a4 4 0 118 0v3"/></svg>`,
        },
        {
          title: "Magic DNS",
          body: "Reach peers by name under the .ray domain. ping alice.ray works from anywhere on the network, no IPs to memorize.",
          icon: `<svg class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="1.7" viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"/><path stroke-linecap="round" d="M3 12h18M12 3c3 3 3 15 0 18M12 3c-3 3-3 15 0 18"/></svg>`,
        },
        {
          title: "File sharing",
          body: "Send files straight between peers over the mesh: content-addressed, hash-verified, no cloud and no size limits.",
          icon: `<svg class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="1.7" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M12 16V4m0 0L8 8m4-4l4 4M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2"/></svg>`,
        },
        {
          title: "Access control",
          body: "Put separate trust domains in separate networks, so peers reach only hosts they share one with. A firewall on each device adds rules by direction and port, and coordinators can suggest them across a network.",
          icon: `<svg class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="1.7" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M12 3l7 3v5c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6l7-3z"/><path stroke-linecap="round" d="M9.5 12l1.8 1.8L15 10"/></svg>`,
        },
        {
          title: "Networks as code",
          body: "Describe the whole mesh in one YAML file: which networks exist, which host may reach which port. ray apply creates what's missing and mints the invites.",
          icon: `<svg class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="1.7" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M9 8l-4 4 4 4m6-8l4 4-4 4"/><path stroke-linecap="round" d="M13 5l-2 14"/></svg>`,
        },
        {
          title: "Tor transport",
          body: "Route traffic over Tor for IP-level anonymity. Runs alongside the default relay, and iroh picks the best path per peer.",
          icon: `<svg class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="1.7" viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="4"/><path stroke-linecap="round" d="M12 3v5m0 8v5"/></svg>`,
        },
      ],
    };
  },

  computed: {
    installer() {
      const installer = INSTALLERS[this.os];
      return this.os === "macos"
        ? { ...installer, href: this.macosDownloadUrl }
        : installer;
    },
    otherInstallers() {
      return Object.values(INSTALLERS).filter(({ os }) => os !== this.os);
    },
  },

  mounted() {
    latestMacosDownload().then((url) => {
      this.macosDownloadUrl = url;
    });
    const reduce = this.reduceMotion;

    this.setupReveals(reduce);

    if (reduce) {
      // Show everything immediately, no orchestration.
      [
        this.$refs.eyebrow,
        this.$refs.line1,
        this.$refs.line2,
        this.$refs.subtext,
        this.$refs.cta,
        this.$refs.cloud,
      ].forEach((el) => el && (el.style.opacity = 1));
      this.output = true;
      this.peers = this.allPeers();
      this.done = true;
      return;
    }

    // Orchestrated page-load sequence. Held on the instance so beforeUnmount
    // can kill it: otherwise it keeps ticking (and fires runTerminal against a
    // destroyed component) after navigating away, which can wedge GSAP and
    // leave the next view's reveal tween stuck.
    const tl = gsap.timeline({ defaults: { ease: "power3.out" } });
    this._tl = tl;
    tl.to(this.$refs.eyebrow, { opacity: 1, duration: 0.5 })
      .from(this.$refs.line1, { y: 24, duration: 0.7 }, "<")
      .to(this.$refs.line1, { opacity: 1, duration: 0.7 }, "<")
      .from(this.$refs.line2, { y: 24, duration: 0.7 }, "-=0.45")
      .to(this.$refs.line2, { opacity: 1, duration: 0.7 }, "<")
      .from(this.$refs.subtext, { y: 16, duration: 0.6 }, "-=0.3")
      .to(this.$refs.subtext, { opacity: 1, duration: 0.6 }, "<")
      .from(this.$refs.cta, { y: 16, duration: 0.6 }, "-=0.35")
      .to(this.$refs.cta, { opacity: 1, duration: 0.6 }, "<")
      .to(this.$refs.cloud, { opacity: 1, duration: 0.7 }, "-=0.5")
      .add(() => this.runTerminal());
  },

  beforeUnmount() {
    if (this._tl) this._tl.kill();
    if (this._observer) this._observer.disconnect();
    this._timers.forEach(clearTimeout);
  },

  created() {
    this._timers = [];
  },

  methods: {
    selectInstaller(os) {
      this.os = os;
      this.copied = false;
      clearTimeout(this._copyTimer);
    },
    async copyInstall() {
      const cmd = this.installer.cmd;
      try {
        await navigator.clipboard.writeText(cmd);
      } catch {
        // Fallback for older browsers / non-secure contexts.
        const el = document.createElement("textarea");
        el.value = cmd;
        el.style.position = "fixed";
        el.style.opacity = "0";
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
      }
      this.copied = true;
      clearTimeout(this._copyTimer);
      this._copyTimer = setTimeout(() => {
        this.copied = false;
      }, 2000);
    },
    allPeers() {
      return [
        { name: "alice.gaming.ray", note: "you · coordinator", rtt: "" },
        { name: "bob.gaming.ray", note: "connected", rtt: "12ms" },
        { name: "carol.gaming.ray", note: "connected", rtt: "31ms" },
      ];
    },

    runTerminal() {
      const at = (fn, ms) => this._timers.push(setTimeout(fn, ms));
      at(() => (this.output = true), 600);
      const peers = this.allPeers();
      peers.forEach((p, i) => at(() => this.peers.push(p), 1500 + i * 650));
      at(() => (this.done = true), 1500 + peers.length * 650 + 200);
    },

    setupReveals(reduce) {
      const els = this.$el.querySelectorAll(".reveal");
      if (reduce || !("IntersectionObserver" in window)) {
        els.forEach((el) => el.classList.add("is-visible"));
        return;
      }
      this._observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add("is-visible");
              this._observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.15 }
      );
      els.forEach((el) => this._observer.observe(el));
    },
  },
};
</script>

<style scoped>
.reveal {
  opacity: 0;
  transform: translateY(22px);
  transition: opacity 0.7s ease, transform 0.7s ease;
}
.reveal.is-visible {
  opacity: 1;
  transform: none;
}
/* Stagger cards within a grid a touch. */
.reveal.is-visible {
  transition-delay: 0.05s;
}

@media (prefers-reduced-motion: reduce) {
  .reveal {
    opacity: 1;
    transform: none;
    transition: none;
  }
}
</style>
